import { useState } from "react";
import { ImagePlus, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { FIELD_LABELS } from "@/admin/config";
import { api, formatError } from "@/lib/api";

const isImageKey = (k) => /image|photo|logo|favicon|thumbnail|cover|gallery/i.test(k);
const isLongKey = (k) => /description|long_|bio|summary|challenge|strategy|execution|results|quote|excerpt|notes|subtitle/i.test(k);
const isHtmlKey = (k) => k === "content";

export default function FieldEditor({ fieldKey, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const label = FIELD_LABELS[fieldKey] || fieldKey.replace(/_/g, " ");
  const inputCls = "w-full bg-background border border-border px-3 py-2 text-sm focus:border-foreground outline-none transition-colors";

  const uploadImages = async (files, append = false) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of selected) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/admin/upload-image", body);
        urls.push(data.url);
        toast.success(`${file.name} dioptimalkan menjadi WebP ${data.width}Ã—${data.height} (${data.saved_percent}% lebih kecil)`);
      }
      onChange(append ? [...(Array.isArray(value) ? value : []), ...urls] : urls[0]);
    } catch (error) {
      toast.error(formatError(error.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  // boolean
  if (typeof value === "boolean") {
    return (
      <label className="flex min-h-11 items-center justify-between border border-border px-3 py-2.5 cursor-pointer" data-testid={`field-${fieldKey}`}>
        <span className="text-sm font-medium capitalize">{label}</span>
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
      </label>
    );
  }

  // number
  if (typeof value === "number" || fieldKey === "price_min" || fieldKey === "price_max") {
    return (
      <div data-testid={`field-${fieldKey}`}>
        <label className="overline text-[10px] text-muted-foreground capitalize">{label}</label>
        <input type="number" className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />
      </div>
    );
  }

  // arrays
  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === "string");
    if (allStrings) {
      return (
        <div data-testid={`field-${fieldKey}`}>
          <label className="overline text-[10px] text-muted-foreground capitalize">{label} <span className="text-muted-foreground/60">(pisahkan dengan koma)</span></label>
          <textarea rows={2} className={inputCls} value={value.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
          {isImageKey(fieldKey) && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <label className={`btn-grid-outline inline-flex cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 ${uploading ? "pointer-events-none opacity-50" : ""}`}>
                {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {uploading ? "Mengoptimalkanâ€¦" : "Upload Gambar"}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only" disabled={uploading} onChange={(e) => uploadImages(e.target.files, true)} />
              </label>
              <span className="text-[10px] text-muted-foreground">Otomatis WebP, maks. 1920 px</span>
            </div>
          )}
          {isImageKey(fieldKey) && value.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">{value.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 object-cover border border-border" />)}</div>
          )}
        </div>
      );
    }
    // array of objects -> JSON
    return <JsonField fieldKey={fieldKey} label={label} value={value} onChange={onChange} inputCls={inputCls} />;
  }

  // objects -> JSON
  if (value && typeof value === "object") {
    return <JsonField fieldKey={fieldKey} label={label} value={value} onChange={onChange} inputCls={inputCls} />;
  }

  // image string
  if (isImageKey(fieldKey)) {
    return (
      <div data-testid={`field-${fieldKey}`}>
        <label className="overline text-[10px] text-muted-foreground capitalize">{label}</label>
        <div className="flex gap-2">
          <input className={`${inputCls} min-w-0 flex-1`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." />
          <label className={`btn-grid-outline inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 ${uploading ? "pointer-events-none opacity-50" : ""}`}>
            {uploading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            <span className="hidden sm:inline">{uploading ? "Memprosesâ€¦" : "Upload"}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" disabled={uploading} onChange={(e) => uploadImages(e.target.files)} data-testid={`upload-${fieldKey}`} />
          </label>
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">JPG, PNG, atau WebP â€¢ otomatis diperkecil ke maks. 1920 px</p>
        {value && <img src={value} alt="" className="mt-2 w-full max-w-[200px] h-28 object-cover border border-border" />}
      </div>
    );
  }

  // html / long text
  if (isHtmlKey(fieldKey) || isLongKey(fieldKey)) {
    return (
      <div data-testid={`field-${fieldKey}`}>
        <label className="overline text-[10px] text-muted-foreground capitalize">{label}</label>
        <textarea rows={isHtmlKey(fieldKey) ? 10 : 3} className={`${inputCls} font-mono-x text-xs leading-relaxed`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }

  // seo preview hint
  const isSeo = fieldKey === "seo_title" || fieldKey === "seo_description";
  return (
    <div data-testid={`field-${fieldKey}`}>
      <label className="overline text-[10px] text-muted-foreground capitalize">{label}</label>
      <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      {isSeo && <p className={`text-[10px] mt-1 ${(value || "").length > (fieldKey === "seo_title" ? 60 : 160) ? "text-destructive" : "text-muted-foreground"}`}>{(value || "").length} karakter</p>}
    </div>
  );
}

function JsonField({ fieldKey, label, value, onChange, inputCls }) {
  return (
    <div data-testid={`field-${fieldKey}`}>
      <label className="overline text-[10px] text-muted-foreground capitalize">{label}</label>
      <textarea rows={6} className={`${inputCls} font-mono-x text-xs`} defaultValue={JSON.stringify(value, null, 2)}
        onBlur={(e) => { try { onChange(JSON.parse(e.target.value)); } catch { /* ignore invalid */ } }} />
      <p className="text-[10px] text-muted-foreground mt-1">Format JSON — perubahan tersimpan saat klik di luar kolom.</p>
    </div>
  );
}
